import {
  LayoutDashboard, Users, PawPrint, Filter, CalendarDays, CalendarRange,
  Scissors, ShoppingCart, FileText, CreditCard, Package, UserCog, BarChart3, Settings, Building2,
  Crown, Layers, Ticket, Award, PieChart, MessageSquare, Sparkles, UsersRound, ShieldCheck,
  Stethoscope, BedDouble, GraduationCap, Wallet, Upload, PhoneCall, Activity, Gauge, Zap, Target, TrendingUp,
  Boxes, Briefcase, Headset,
} from "lucide-react";
import { hasPerm } from "@/lib/perms";

// Sidebar groups (order = display order). Dashboard is a standalone, non-collapsible entry.
export const NAV_GROUPS = [
  { key: "dashboard", label: "Dashboard", standalone: true },
  { key: "care", label: "Bookings & Care", icon: CalendarDays },
  { key: "billing", label: "Billing & Payments", icon: CreditCard },
  { key: "growth", label: "Customers & Growth", icon: Users },
  { key: "inventory", label: "Inventory & Store", icon: Boxes },
  { key: "comms", label: "Automation & Communications", icon: MessageSquare },
  { key: "team", label: "Team & Operations", icon: Briefcase },
  { key: "reports", label: "Reports & Insights", icon: BarChart3 },
  { key: "admin", label: "Settings & Administration", icon: Settings },
];

// Extra search terms for sidebar Quick Search (by route).
export const NAV_KEYWORDS = {
  "/dashboard": "home overview urgent collections",
  "/command-center": "overview kpi owner",
  "/business-planning": "goals targets plan",
  "/business-review": "profit review margin",
  "/business-actions": "profit actions tasks",
  "/customers": "clients owners crm parents",
  "/pets": "animals dogs cats",
  "/leads": "enquiries prospects inquiry",
  "/shally": "shally ai receptionist calls call intelligence recordings transcripts follow-ups",
  "/segments": "audience groups marketing",
  "/bookings": "appointments schedule",
  "/grooming": "groom spa bath haircut",
  "/veterinary": "vet clinic doctor medical vaccination",
  "/boarding": "stay hostel kennel room",
  "/training": "classes sessions obedience",
  "/calendar": "schedule appointments",
  "/services": "service catalog price list",
  "/service-profitability": "margin profit cost",
  "/memberships": "plans subscription",
  "/packages": "bundles sessions prepaid",
  "/coupons": "discount promo code offer",
  "/loyalty": "points rewards",
  "/pos": "checkout sale billing cash register counter",
  "/invoices": "bills billing receipt gst",
  "/payments": "collections receipts upi cash card refunds",
  "/payment-ops": "reconciliation refunds gateway razorpay",
  "/expenses": "costs spend bills vendor",
  "/accounts": "vendors suppliers purchase bills payables payments advances gst ledger accounts",
  "/ca": "ca chartered accountant firm clients review audit",
  "/communications": "messages whatsapp sms email inbox",
  "/follow-ups": "calls reminders callback",
  "/action-center": "tasks alerts todo",
  "/automation": "workflows rules triggers reminders notifications",
  "/staff-performance": "productivity commission employee",
  "/communications/templates": "message templates whatsapp email sms",
  "/communications/automation": "message automation reminders notifications campaigns",
  "/invoice-settings": "gst tax invoice numbering prefix",
  "/billing": "subscription plan upgrade",
  "/inventory": "products stock store consumption movements suppliers",
  "/staff": "employees groomers vets",
  "/reports": "analytics sales collections revenue",
  "/data-quality": "duplicates cleanup issues",
  "/team": "users invite employees",
  "/roles": "permissions access rbac",
  "/approvals": "approve requests pending",
  "/approvals-settings": "controls policy limits",
  "/settings": "business branch configuration integrations",
  "/settings/business": "business profile address contact gstin pan logo operating hours holidays timezone",
  "/data-import": "upload csv excel migrate",
};

// flag: feature_flags key that must be true (null = always on)
// perm: RBAC permission the user must hold for the item to appear (null = any)
export const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, flag: null, group: "dashboard" },
  { to: "/command-center", label: "Command Center", icon: LayoutDashboard, flag: "command_center", perm: "reports.view", group: "reports" },
  { to: "/business-planning", label: "Business Planning", icon: Target, flag: "business_planning", perm: "reports.view", group: "reports" },
  { to: "/business-review", label: "Business Review", icon: TrendingUp, flag: "profit_intelligence", perm: "expenses.view", group: "reports" },
  { to: "/business-actions", label: "Business Actions", icon: Zap, flag: "profit_intelligence", perm: "expenses.view", group: "reports" },
  { to: "/customers", label: "Customers", icon: Users, flag: "customers", group: "growth" },
  { to: "/pets", label: "Pets", icon: PawPrint, flag: "pets", group: "growth" },
  { to: "/leads", label: "Leads", icon: Filter, flag: "leads", group: "growth" },
  { to: "/shally", label: "AI Receptionist", icon: Headset, flag: "leads", perm: "calls.view", group: "growth" },
  { to: "/segments", label: "Segments", icon: PieChart, flag: "marketing", group: "growth" },
  { to: "/bookings", label: "Bookings", icon: CalendarDays, flag: "bookings", group: "care" },
  { to: "/grooming", label: "Grooming", icon: Sparkles, flag: "grooming", group: "care" },
  { to: "/veterinary", label: "Veterinary", icon: Stethoscope, flag: "vet", perm: "vet.view", group: "care" },
  { to: "/boarding", label: "Boarding", icon: BedDouble, flag: "boarding", perm: "boarding.view", group: "care" },
  { to: "/training", label: "Training", icon: GraduationCap, flag: "training", perm: "training.view", group: "care" },
  { to: "/calendar", label: "Calendar", icon: CalendarRange, flag: "calendar", group: "care" },
  { to: "/services", label: "Services", icon: Scissors, flag: "services", group: "care" },
  { to: "/service-profitability", label: "Service Profitability", icon: PieChart, flag: "service_profitability", perm: "reports.view", group: "reports" },
  { to: "/memberships", label: "Memberships", icon: Crown, flag: "memberships", group: "growth" },
  { to: "/packages", label: "Packages", icon: Layers, flag: "packages", group: "growth" },
  { to: "/coupons", label: "Coupons", icon: Ticket, flag: "loyalty", group: "growth" },
  { to: "/loyalty", label: "Loyalty", icon: Award, flag: "loyalty", group: "growth" },
  { to: "/pos", label: "POS", icon: ShoppingCart, flag: "pos", group: "billing" },
  { to: "/invoices", label: "Invoices", icon: FileText, flag: "invoices", group: "billing" },
  { to: "/payments", label: "Payments", icon: CreditCard, flag: "payments", group: "billing" },
  { to: "/payment-ops", label: "Payment Ops", icon: ShieldCheck, flag: "payments", group: "billing" },
  { to: "/expenses", label: "Expenses", icon: Wallet, flag: null, perm: "expenses.view", altPerm: "payables.pay_request", group: "team" },
  { to: "/accounts", label: "Accounts & Purchase", icon: Briefcase, flag: null, perm: "payables.view", group: "billing" },
  { to: "/ca", label: "CA Firm Workspace", icon: Briefcase, flag: null, perm: null, group: "billing" },
  { to: "/communications", label: "Communications", icon: MessageSquare, flag: null, group: "comms" },
  { to: "/follow-ups", label: "Follow-ups", icon: PhoneCall, flag: null, perm: "communications.send", group: "comms" },
  { to: "/action-center", label: "Action Center", icon: Activity, flag: null, perm: "reports.view", group: "team" },
  { to: "/automation", label: "Automation", icon: Zap, flag: null, perm: "settings.view", group: "comms" },
  { to: "/staff-performance", label: "Staff Performance", icon: Gauge, flag: null, perm: "reports.view", group: "team" },
  { to: "/communications/templates", label: "Msg Templates", icon: MessageSquare, flag: null, perm: "communications.view", group: "comms" },
  { to: "/communications/automation", label: "Msg Automation", icon: MessageSquare, flag: null, perm: "communications.view", group: "comms" },
  { to: "/invoice-settings", label: "Invoice Settings", icon: FileText, flag: null, perm: "billing.view", group: "admin" },
  { to: "/billing", label: "Billing & Plan", icon: CreditCard, flag: null, perm: "settings.view", group: "admin" },
  { to: "/inventory", label: "Inventory", icon: Package, flag: "inventory", group: "inventory" },
  { to: "/staff", label: "Staff", icon: UserCog, flag: "staff", group: "team" },
  { to: "/reports", label: "Reports", icon: BarChart3, flag: "reports", perm: "reports.view", group: "reports" },
  { to: "/data-quality", label: "Data Quality", icon: ShieldCheck, flag: null, perm: "reports.view", serverFlag: "data_quality", group: "reports" },
  { to: "/team", label: "Team", icon: UsersRound, flag: null, perm: "staff.create", group: "team" },
  { to: "/roles", label: "Roles", icon: ShieldCheck, flag: null, perm: "staff.manage_roles", group: "team" },
  { to: "/approvals", label: "Approval Center", icon: ShieldCheck, flag: null, perm: "approval.approve", group: "team" },
  { to: "/approvals-settings", label: "Approvals & Controls", icon: ShieldCheck, flag: null, perm: "settings.edit", group: "admin" },
  { to: "/settings", label: "Settings", icon: Settings, flag: null, perm: "settings.view", group: "admin" },
  { to: "/settings/business", label: "Business Settings", icon: Building2, flag: null, perm: "settings.view", group: "admin" },
  { to: "/data-import", label: "Data Import", icon: Upload, flag: null, perm: "data_import.manage", group: "admin" },
];


// Role-scoped sidebar for system roles (custom roles keep permission-based menus).
// The backend enforces the same limits via RBAC permissions; this only narrows the menu + direct URLs.
export const ROLE_NAV = {
  receptionist: { groups: ["dashboard", "billing", "growth", "care", "inventory", "team"], regroup: { "/follow-ups": "growth" } },
  vet: { items: ["/dashboard", "/veterinary", "/customers", "/pets", "/bookings", "/calendar"] },
  // Finance roles: only the dashboard plus pages they hold an explicit permission for (no perm-less shared pages)
  accounts: { permOnly: true },
  purchase: { permOnly: true },
};

const roleScope = (user) => (!user?.custom_role_id && ROLE_NAV[user?.role]) || null;

// altPerm: a second permission that also opens the page (e.g. Procurement raising expense requests)
export const navPermOk = (user, n) => !n.perm || hasPerm(user, n.perm) || (!!n.altPerm && hasPerm(user, n.altPerm));

export const applyRoleNav = (items, user) => {
  const r = roleScope(user);
  if (!r) return items;
  return items
    .map((n) => (r.regroup?.[n.to] ? { ...n, group: r.regroup[n.to] } : n))
    .filter((n) => (!r.groups || r.groups.includes(n.group)) && (!r.items || r.items.includes(n.to)) && (!r.permOnly || n.to === "/dashboard" || (!!n.perm && navPermOk(user, n))));
};

export const navItemFor = (items, path) =>
  items.filter((i) => path === i.to || path.startsWith(i.to + "/")).sort((a, b) => b.to.length - a.to.length)[0];

// True when a role-scoped user opens a sidebar module outside their scope (e.g. by typing the URL).
export const roleBlocksPath = (user, path) => {
  if (!roleScope(user)) return false;
  const item = navItemFor(NAV, path);
  return !!item && (applyRoleNav([item], user).length === 0 || !navPermOk(user, item));
};
